/**
 * Multipart file upload API.
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import { buildFilePart } from '../../platform/filePart';
import { API_ENDPOINTS } from '../endpoints';
import type {
  FileUploadRequestMeta,
  FileUploadResponseBody,
} from './fileUploadTypes';

const guessMimeType = (uri: string): string => {
  const lower = uri.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
};

const guessFileName = (uri: string, fallback: string): string => {
  const parts = uri.split(/[/\\]/);
  const last = parts[parts.length - 1];
  if (last && last.includes('.')) return last;
  return fallback;
};

export interface LocalFileUploadPart {
  uri: string;
  type?: string;
  name?: string;
  /** Web only: the picked File/Blob, which browsers require in FormData. */
  blob?: Blob | null;
}

export interface LocalFileUploadInput extends LocalFileUploadPart {
  meta: FileUploadRequestMeta;
}

export interface LocalFilesUploadInput {
  files: LocalFileUploadPart[];
  meta: FileUploadRequestMeta;
}

const appendUploadMeta = (formData: FormData, meta: FileUploadRequestMeta): void => {
  formData.append('Module', meta.module);
  formData.append('ContentType', meta.contentType);
  if (meta.referenceId) {
    formData.append('ReferenceId', meta.referenceId);
  }
  if (meta.folderName) {
    formData.append('FolderName', meta.folderName);
  }
  if (meta.uploadedBy) {
    formData.append('UploadedBy', meta.uploadedBy);
  }
  if (meta.metadata) {
    formData.append('Metadata', JSON.stringify(meta.metadata));
  }
};

const fileUploadApi = {
  /**
   * Upload one or more local files (`Files` field, repeated for multi-file).
   * Used for Comparison2 / Comparison4 combine uploads (exactly 2 or 4 images).
   */
  uploadLocalFiles: (
    input: LocalFilesUploadInput,
  ): Promise<AxiosResponse<FileUploadResponseBody>> => {
    const { files, meta } = input;
    if (!files.length) {
      return Promise.reject(new Error('At least one file is required for upload'));
    }

    const formData = new FormData();
    appendUploadMeta(formData, meta);

    files.forEach((file, index) => {
      const uri = file.uri;
      formData.append(
        'Files',
        buildFilePart({
          uri,
          type: file.type || guessMimeType(uri),
          name: file.name || guessFileName(uri, `upload-${index + 1}.jpg`),
          blob: file.blob,
        }),
      );
    });

    return apiClient.post<FileUploadResponseBody>(
      API_ENDPOINTS.FILE_UPLOAD.UPLOAD,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  },

  uploadLocalFile: (
    input: LocalFileUploadInput,
  ): Promise<AxiosResponse<FileUploadResponseBody>> => {
    const { uri, type, name, blob, meta } = input;
    return fileUploadApi.uploadLocalFiles({
      files: [{ uri, type, name, blob }],
      meta,
    });
  },
};

export default fileUploadApi;
